// Ad attribution (UTMs) for donations.
//
// A tracked link's params are kept in both sessionStorage and localStorage, as one
// set: a newer tracked link replaces the whole set in both at once (keys it does not
// carry are removed, never kept from the older link). Reads use sessionStorage, and
// fall back to localStorage only when the session has none — e.g. the donor came
// back in a new tab. The localStorage copy is ignored after 7 days.

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_id",
  "utm_content",
  "utm_term",
  "utm_matchtype", // Google keyword match type (e: exact, p: phrase, b: broad)
  "ad_id",
  "adset_id",
  "adgroup_id",
  "utm_landing_url", // the exact URL the donor landed on
  "cutm_platform", // our own: wa / email / p_donateonce
];

const SAVED_AT_KEY = "utm_saved_at";
const LOCAL_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Storage can throw (private mode, blocked site data) — attribution must never break the page.
const safe = (fn, fallback) => {
  try {
    return fn();
  } catch (_) {
    return fallback;
  }
};

const clear = (storage) => {
  UTM_KEYS.forEach((key) => storage.removeItem(key));
  storage.removeItem(SAVED_AT_KEY);
};

const readSet = (storage) =>
  UTM_KEYS.reduce((set, key) => {
    const value = storage.getItem(key);
    if (value) set[key] = value;
    return set;
  }, {});

/** Call on a full page load: if the URL is a tracked link, replace the stored set in both storages. */
export function captureUtmsFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const isTracked = [...params.keys()].some(
    (key) => key.startsWith("utm_") || UTM_KEYS.includes(key)
  );
  if (!isTracked) return;

  const set = {};
  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) set[key] = value;
  });
  set.utm_landing_url = window.location.href;

  [window.sessionStorage, window.localStorage].forEach((storage) =>
    safe(() => {
      clear(storage);
      Object.entries(set).forEach(([key, value]) => storage.setItem(key, value));
    })
  );
  safe(() => window.localStorage.setItem(SAVED_AT_KEY, String(Date.now())));
}

/** The stored set: sessionStorage's, else localStorage's if under 7 days old, else {}. */
export function getStoredUtms() {
  const session = safe(() => readSet(window.sessionStorage), {});
  if (Object.keys(session).length) return session;

  return safe(() => {
    const local = window.localStorage;
    const savedAt = Number(local.getItem(SAVED_AT_KEY));
    if (!savedAt || Date.now() - savedAt > LOCAL_TTL_MS) {
      clear(local);
      return {};
    }
    return readSet(local);
  }, {});
}
